import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import "./Notifications.css";

const NotificationList = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // 'all', 'unread'
    const navigate = useNavigate();

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const endpoint = filter === "unread" ? "/notifications/unread/" : "/notifications/";
            const response = await API.get(endpoint);
            setNotifications(response.data);
        } catch (err) {
            console.error("Error fetching notifications:", err);
            toast.error("Failed to fetch notifications.");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleMarkAsRead = async (notificationId) => {
        try {
            await API.post(`/notifications/${notificationId}/mark_as_read/`);
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
        } catch (err) {
            console.error("Error marking notification as read:", err);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await API.post("/notifications/mark_read/", {});
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            toast.success("All notifications marked as read.");
        } catch (err) {
            console.error("Error marking all as read:", err);
            toast.error("Failed to mark notifications as read.");
        }
    };

    const handleClearRead = async () => {
        try {
            await API.delete("/notifications/clear_all/");
            setNotifications(prev => prev.filter(n => !n.read));
            toast.success("Cleared all read notifications.");
        } catch (err) {
            console.error("Error clearing notifications:", err);
            toast.error("Failed to clear notifications.");
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            handleMarkAsRead(notification.id);
        }
        if (notification.project_id) {
            navigate(`/Projects/${notification.project_id}`);
        }
    };

    const getTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return "just now";
        if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
        return date.toLocaleDateString();
    };

    const getActionIcon = (action) => {
        const icons = {
            created: "fa-plus-circle text-success",
            updated: "fa-edit text-info",
            deleted: "fa-trash text-danger",
            uploaded: "fa-upload text-primary",
            invited: "fa-user-plus text-warning",
            joined: "fa-user-check text-success",
            left: "fa-user-minus text-secondary",
            assigned: "fa-tasks text-primary",
            unassigned: "fa-user-times text-secondary",
            status_changed: "fa-exchange-alt text-info",
        };
        return icons[action] || "fa-bell";
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="notifications-page">
            <Navbar />
            <div className="container py-4">
                <div className="notifications-container">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>Notifications</h2>
                        <div className="d-flex gap-2">
                            {unreadCount > 0 && (
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={handleMarkAllAsRead}
                                >
                                    Mark all as read
                                </button>
                            )}
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={handleClearRead}
                            >
                                Clear read
                            </button>
                        </div>
                    </div>

                    {/* Filter Tabs */}
                    <ul className="nav nav-tabs mb-4">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${filter === "all" ? "active" : ""}`}
                                onClick={() => setFilter("all")}
                            >
                                All
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${filter === "unread" ? "active" : ""}`}
                                onClick={() => setFilter("unread")}
                            >
                                Unread {unreadCount > 0 && `(${unreadCount})`}
                            </button>
                        </li>
                    </ul>

                    {/* Notifications List */}
                    <div className="notifications-card">
                        {loading ? (
                            <div className="notification-empty">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">
                                <i className="fa fa-bell-slash"></i>
                                <p>
                                    {filter === "unread"
                                        ? "No unread notifications"
                                        : "No notifications yet"}
                                </p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.read ? "unread" : ""}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-icon">
                                        <i className={`fa ${getActionIcon(notification.action)}`}></i>
                                    </div>
                                    <div className="notification-content">
                                        <p className="notification-text">
                                            <strong>{notification.user?.username}</strong>{" "}
                                            {notification.description}
                                        </p>
                                        <div className="notification-meta">
                                            {notification.project_name && (
                                                <span className="notification-project">
                                                    <i className="fa fa-folder me-1"></i>
                                                    {notification.project_name}
                                                </span>
                                            )}
                                            <span className="notification-time">
                                                {getTimeAgo(notification.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    {!notification.read && (
                                        <span className="notification-unread-dot"></span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationList;
